import {LetrusApi} from '@letrustech/letrus-api-interfaces';
import {CompositionAid} from '@letrustech/letrus-api-interfaces/dist/interfaces/letrus_api';
import {CompositionProps} from 'components/StatisticsSidebar';
import {fromJS, List} from 'immutable';
import {useNavigate} from 'react-router-dom';
import countTextEditorLines from 'utils/functions/count-text-editor-lines';
import countWords from 'utils/functions/count-words';
import {
  APIMarkerScore,
  JsonStatsAsync,
  LetrusScore,
  MarkerOccurrence,
  MarkerScore,
} from 'utils/types/markers';

export interface JsonStatsAsyncPostData {
  processComments: boolean;
  compositionRaw?: string;
  markersToProcess: string[];
}

type TestResultFeedbackData = {
  feedback: string;
  sub_feedback: string;
};

type MarkersType = 'best_markers' | 'worst_markers';

interface TestResultScoreData {
  bestMarkers: string[];
  worstMarkers: string[];
  bestMarkersScore: List<ImmutableMap<MarkerScore>>;
  worstMarkersScore: List<ImmutableMap<MarkerScore>>;
}

interface UseCompletedTestReportProps {
  compositionDetails?: ImmutableMap<CompositionProps>;
  jsonStatsAsync?: ImmutableMap<JsonStatsAsync>;
}

interface UseCompletedTestReportReturn {
  wordsNumber?: number;
  linesNumber?: number;
  compositionRaw?: string;
  jsonStatsAsyncPostData?: JsonStatsAsyncPostData;
  instructionTitle?: string;
  compositionGenre?: string;
  aids?: CompositionAid[];
  testResultFeedbackData?: TestResultFeedbackData;
  testResultScoreData?: TestResultScoreData;
  simpleExerciseCardData: any;
}

function useCompletedTestReport({
  compositionDetails,
  jsonStatsAsync,
}: UseCompletedTestReportProps): UseCompletedTestReportReturn {
  const navigate = useNavigate();
  const wordsNumber = countWords(compositionDetails?.get('composition_raw'));
  const linesNumber = countTextEditorLines(
    getAllParagraphsOfComposition(compositionDetails?.get('composition_raw')),
  );
  const compositionRaw = compositionDetails?.get('composition_raw');

  const shownStatsList: ImmutableMap<LetrusApi.CompositionGenreShownStat>[] =
    compositionDetails?.getIn(['genre', 'shown_stats']);

  const letrusScore = jsonStatsAsync?.getIn(['jsonMetadata', 'letrus_score']);
  const comments = jsonStatsAsync?.get('comments');

  const jsonStatsAsyncPostData = compositionDetails && {
    processComments: true,
    compositionRaw,
    markersToProcess:
      shownStatsList?.map(
        (
          shownStat: ImmutableMap<LetrusApi.CompositionGenreShownStat>,
        ): string => {
          return getShownStatName(shownStat);
        },
      ) || [],
  };
  const instructionTitle = compositionDetails?.getIn([
    'instruction_id',
    'title',
  ]);
  const compositionGenre = compositionDetails?.getIn(['test', 'genre']);
  // Doesn't work with immutable data
  const aids = compositionDetails?.get('aids')?.toJS();

  const testResultFeedbackData: TestResultFeedbackData = {
    feedback: letrusScore?.get('feedback') ?? '',
    sub_feedback: letrusScore?.get('sub_feedback') ?? '',
  };

  const bestMarkers = getMarkersScore('best_markers');
  const worstMarkers = getMarkersScore('worst_markers');
  const testResultScoreData: TestResultScoreData | undefined = letrusScore &&
    bestMarkers && {
      bestMarkers: bestMarkers.map((bestMarker) =>
        bestMarker.get('public_name'),
      ),
      worstMarkers: worstMarkers.map((worstMarker) =>
        worstMarker.get('public_name'),
      ),
      bestMarkersScore: bestMarkers.map((bestMarker) => {
        return fromJS({
          goal: bestMarker.get('goal'),
          value: bestMarker.get('value'),
          max: bestMarker.get('max_target'),
          min: bestMarker.get('min_target'),
          label: bestMarker.get('public_name'),
          comment: getMarkerComment(bestMarker.get('name')),
          name: bestMarker.get('name'),
          markerOccurrence: getMarkerOccurrences(bestMarker.get('name')),
        });
      }),
      worstMarkersScore: worstMarkers.map((worstMarker) => {
        return fromJS({
          goal: worstMarker.get('goal'),
          value: worstMarker.get('value'),
          max: worstMarker.get('max_target'),
          min: worstMarker.get('min_target'),
          label: worstMarker.get('public_name'),
          comment: getMarkerComment(worstMarker.get('name')),
          name: worstMarker.get('name'),
          markerOccurrence: getMarkerOccurrences(worstMarker.get('name')),
        });
      }),
    };

  const simpleExerciseCardData: any = {
    icon: 'file-alt',
    description: 'Aprimore seu texto fazendo exercícios.',
    title: 'Exercícios',
    buttonText: 'Fazer exercícios',
    onClickButton: () => {
      navigate('/atividades/pesquisa/1');
    },
  };

  function getShownStatName(
    shownStat: ImmutableMap<LetrusApi.CompositionGenreShownStat>,
  ) {
    if (shownStat?.get('marker')) {
      return shownStat.getIn(['marker', 'varname']) || '';
    }
    return '';
  }

  function getAllParagraphsOfComposition(compositionRaw?: string) {
    const paragraphs = compositionRaw?.split('\n\n');
    return paragraphs;
  }

  function getMarkersScore(
    scoreType: MarkersType,
  ): ImmutableList<ImmutableMap<APIMarkerScore>> {
    return letrusScore?.get(`${scoreType}` as keyof LetrusScore) ?? fromJS([]);
  }

  function getMarkerComment(marker: string) {
    return comments?.getIn([`${marker}`, 'comment']) ?? '';
  }

  function getMarkerOccurrences(
    marker: string,
  ): List<ImmutableMap<MarkerOccurrence>> {
    return jsonStatsAsync?.getIn(['markers_occurrences', marker]) ?? [];
  }

  return {
    testResultFeedbackData,
    testResultScoreData,
    wordsNumber,
    linesNumber,
    compositionRaw,
    jsonStatsAsyncPostData,
    instructionTitle,
    compositionGenre,
    aids,
    simpleExerciseCardData,
  };
}

export default useCompletedTestReport;
